import { create } from 'zustand';
import {
  patchResetLoginEmail,
  putNotifications,
  getGuidedValueUserInfo,
  postGuidedValue,
} from '../services/accountApi';
import { getIsImpersonating, getSessionUser, setSessionUser, type SessionUser } from '../utils/session';
import type {
  NotificationType,
  NotificationsPayload,
  GuidedValueItem,
} from '../types/api';

// ─── Notification methods shape ──────────────────────────────────────────────

export interface NotificationMethods {
  NotificationMethodId: number | string | null;
  NotificationEmailAddress: string;
  NotificationTextAreaCode: string;
  NotificationTextPhoneNumber: string;
  NotificationVoiceAreaCode: string;
  NotificationVoicePhoneNumber: string;
  NotificationVoiceExtension: string;
  EnableAllNotifications: boolean;
}

// ─── GuidedValue state ───────────────────────────────────────────────────────

export interface GuidedValueState {
  isUsingGv: boolean;
  loadingGv: boolean;
  gvFetchAttempted: boolean;
  guidedValue: GuidedValueItem | null;
  guidedValueTextAreaCode: string;
  guidedValueTextNumber: string;
  maxorCustomerId: string | null;
}

// ─── Store interface ─────────────────────────────────────────────────────────

export type MyAccountTab = 'security' | 'notifications';

export interface MyAccountStore {
  activeTab: MyAccountTab | null;
  pendingTabChange: MyAccountTab | null;
  isInGuardian: boolean;

  // Security (email change only)
  securityEmail: string;
  originalSecurityEmail: string;
  securitySubmitting: boolean;
  securityError: string | null;
  securitySuccess: boolean;

  // Notifications
  notificationMethods: NotificationMethods;
  notificationTypes: NotificationType[];
  linkToken: string;
  originalNotificationData: string; // JSON snapshot for dirty check
  notificationsSubmitting: boolean;
  notificationsError: string | null;
  notificationsSuccess: boolean;

  // Guided Value (side-effect of notifications)
  gv: GuidedValueState;

  // Actions
  setActiveTab: (tab: MyAccountTab | null) => void;
  confirmTabChange: () => void;
  cancelTabChange: () => void;
  loadFromSession: (user: SessionUser) => void;

  // Security
  setSecurityEmail: (email: string) => void;
  saveSecurityInfo: () => Promise<void>;
  clearSecurityState: () => void;

  // Notifications
  setNotificationMethod: (methodId: number | string) => void;
  setNotificationContact: (
    field: keyof Pick<
      NotificationMethods,
      | 'NotificationEmailAddress'
      | 'NotificationTextAreaCode'
      | 'NotificationTextPhoneNumber'
      | 'NotificationVoiceAreaCode'
      | 'NotificationVoicePhoneNumber'
      | 'NotificationVoiceExtension'
    >,
    value: string
  ) => void;
  setGvNotify: (enabled: boolean) => void;
  setGvTextAreaCode: (value: string) => void;
  setGvTextNumber: (value: string) => void;
  saveNotifications: () => Promise<void>;
  clearNotificationsState: () => void;

  // Guided Value
  fetchGuidedValue: (memberId: string) => Promise<void>;
  updateGuidedValueNotifications: (onlyGv?: boolean) => Promise<void>;

  reset: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildInitialNotificationMethods(user: SessionUser): NotificationMethods {
  if (user.AreNotificationsEnabled === false) {
    return {
      NotificationMethodId: '',
      NotificationEmailAddress: '',
      NotificationTextAreaCode: '',
      NotificationTextPhoneNumber: '',
      NotificationVoiceAreaCode: '',
      NotificationVoicePhoneNumber: '',
      NotificationVoiceExtension: '',
      EnableAllNotifications: false,
    };
  }
  return {
    NotificationMethodId: (user.NotificationMethodId as number | null) ?? null,
    NotificationEmailAddress: (user.NotificationEmailAddress as string) ?? '',
    NotificationTextAreaCode: (user.NotificationTextAreaCode as string) ?? '',
    NotificationTextPhoneNumber: (user.NotificationTextPhoneNumber as string) ?? '',
    NotificationVoiceAreaCode: (user.NotificationVoiceAreaCode as string) ?? '',
    NotificationVoicePhoneNumber: (user.NotificationVoicePhoneNumber as string) ?? '',
    NotificationVoiceExtension: (user.NotificationVoiceExtension as string) ?? '',
    EnableAllNotifications: true,
  };
}

function splitPhone(mobile: string | undefined): { areaCode: string; number: string } {
  if (!mobile) return { areaCode: '', number: '' };
  return {
    areaCode: mobile.substring(0, 3),
    number: mobile.substring(3),
  };
}

// ─── Initial state ───────────────────────────────────────────────────────────

const initialGv: GuidedValueState = {
  isUsingGv: false,
  loadingGv: false,
  gvFetchAttempted: false,
  guidedValue: null,
  guidedValueTextAreaCode: '',
  guidedValueTextNumber: '',
  maxorCustomerId: null,
};

const initialNotificationMethods: NotificationMethods = {
  NotificationMethodId: null,
  NotificationEmailAddress: '',
  NotificationTextAreaCode: '',
  NotificationTextPhoneNumber: '',
  NotificationVoiceAreaCode: '',
  NotificationVoicePhoneNumber: '',
  NotificationVoiceExtension: '',
  EnableAllNotifications: false,
};

type StoreState = Omit<
  MyAccountStore,
  | 'setActiveTab'
  | 'confirmTabChange'
  | 'cancelTabChange'
  | 'loadFromSession'
  | 'setSecurityEmail'
  | 'saveSecurityInfo'
  | 'clearSecurityState'
  | 'setNotificationMethod'
  | 'setNotificationContact'
  | 'setGvNotify'
  | 'setGvTextAreaCode'
  | 'setGvTextNumber'
  | 'saveNotifications'
  | 'clearNotificationsState'
  | 'fetchGuidedValue'
  | 'updateGuidedValueNotifications'
  | 'reset'
>;

const initialState: StoreState = {
  activeTab: null,
  pendingTabChange: null,
  isInGuardian: false,
  securityEmail: '',
  originalSecurityEmail: '',
  securitySubmitting: false,
  securityError: null,
  securitySuccess: false,
  notificationMethods: initialNotificationMethods,
  notificationTypes: [],
  linkToken: '',
  originalNotificationData: '',
  notificationsSubmitting: false,
  notificationsError: null,
  notificationsSuccess: false,
  gv: initialGv,
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useMyAccountStore = create<MyAccountStore>((set, get) => ({
  ...initialState,

  // ─── Tab navigation ────────────────────────────────────────────────────────

  setActiveTab: (tab) => {
    const { activeTab, securityEmail, originalSecurityEmail, notificationMethods, originalNotificationData } = get();

    if (tab === activeTab) return;

    // Dirty check: compare current state to snapshot
    let isDirty = false;
    if (activeTab === 'security') {
      isDirty = securityEmail !== originalSecurityEmail;
    } else if (activeTab === 'notifications') {
      isDirty = JSON.stringify(notificationMethods) !== originalNotificationData;
    }

    if (isDirty) {
      set({ pendingTabChange: tab });
      return;
    }

    set({ activeTab: tab, pendingTabChange: null });
  },

  confirmTabChange: () => {
    const { pendingTabChange } = get();
    if (pendingTabChange) {
      set({ activeTab: pendingTabChange, pendingTabChange: null });
    }
  },

  cancelTabChange: () => {
    set({ pendingTabChange: null });
  },

  // ─── Session load ──────────────────────────────────────────────────────────

  loadFromSession: (user) => {
    const email = (user.EmailAddress as string) ?? '';
    const notificationMethods = buildInitialNotificationMethods(user);
    const notificationTypes = (user.NotificationTypes as NotificationType[]) ?? [];
    const linkToken = (user.LinkToken as string) ?? '';
    const isInGuardian = user.IsInGuardian === true;

    set({
      securityEmail: email,
      originalSecurityEmail: email,
      notificationMethods,
      notificationTypes,
      linkToken,
      isInGuardian,
      originalNotificationData: JSON.stringify(notificationMethods),
    });
  },

  // ─── Security ─────────────────────────────────────────────────────────────

  setSecurityEmail: (email) => {
    set({ securityEmail: email });
  },

  saveSecurityInfo: async () => {
    if (getIsImpersonating()) return;

    const { securityEmail, originalSecurityEmail } = get();

    set({ securitySubmitting: true, securityError: null, securitySuccess: false });

    try {
      const result = await patchResetLoginEmail({
        OriginalEmailAddress: originalSecurityEmail,
        NewEmailAddress: securityEmail,
      });

      if (result.StatusCode === 'SUCCESS') {
        set({
          securitySubmitting: false,
          securitySuccess: true,
          originalSecurityEmail: securityEmail,
        });
      } else {
        set({
          securitySubmitting: false,
          securityError: result.StatusMessage ?? 'An error occurred updating your email.',
        });
      }
    } catch {
      set({
        securitySubmitting: false,
        securityError: 'An error occurred updating your email.',
      });
    }
  },

  clearSecurityState: () => {
    set({ securityError: null, securitySuccess: false });
  },

  // ─── Notifications ────────────────────────────────────────────────────────

  setNotificationMethod: (methodId) => {
    const enableAll = methodId === 6 || methodId === 1001 || methodId === 1003 ||
      methodId === '6' || methodId === '1001' || methodId === '1003';
    set(state => ({
      notificationMethods: {
        ...state.notificationMethods,
        NotificationMethodId: methodId,
        EnableAllNotifications: enableAll,
      },
    }));
  },

  setNotificationContact: (field, value) => {
    set(state => ({
      notificationMethods: { ...state.notificationMethods, [field]: value },
    }));
  },

  setGvNotify: (enabled) => {
    set(state => ({
      gv: {
        ...state.gv,
        guidedValue: state.gv.guidedValue
          ? {
              ...state.gv.guidedValue,
              Preference: { ...state.gv.guidedValue.Preference, GuidedValueNotify: enabled },
            }
          : null,
      },
    }));
  },

  setGvTextAreaCode: (value) => {
    set(state => ({ gv: { ...state.gv, guidedValueTextAreaCode: value } }));
  },

  setGvTextNumber: (value) => {
    set(state => ({ gv: { ...state.gv, guidedValueTextNumber: value } }));
  },

  saveNotifications: async () => {
    if (getIsImpersonating()) return;

    const { notificationMethods, notificationTypes, linkToken, gv } = get();

    // Ensure EnableAllNotifications is set correctly before submit
    const methodId = notificationMethods.NotificationMethodId;
    const enableAll = methodId === 6 || methodId === 1001 || methodId === 1003 ||
      methodId === '6' || methodId === '1001' || methodId === '1003';

    const payload: NotificationsPayload = {
      LinkToken: linkToken,
      NotificationMethodId: methodId as number | null,
      NotificationTypes: notificationTypes,
      EmailAddress: notificationMethods.NotificationEmailAddress,
      // Strip legacy formatting: parentheses from area codes, dashes from phone numbers
      TextAreaCode: notificationMethods.NotificationTextAreaCode.replace(/[()]/g, ''),
      TextPhoneNumber: notificationMethods.NotificationTextPhoneNumber.replace(/-/g, ''),
      VoiceAreaCode: notificationMethods.NotificationVoiceAreaCode.replace(/[()]/g, ''),
      VoicePhoneNumber: notificationMethods.NotificationVoicePhoneNumber.replace(/-/g, ''),
      VoiceExtension: notificationMethods.NotificationVoiceExtension,
    };

    if (enableAll) {
      payload.LinkToken = linkToken; // already set; keeping for clarity
    }

    set({ notificationsSubmitting: true, notificationsError: null, notificationsSuccess: false });

    try {
      const result = await putNotifications(payload);

      if (result.StatusCode === 'SUCCESS') {
        const updatedMethods = { ...notificationMethods, EnableAllNotifications: enableAll };
        set({
          notificationsSubmitting: false,
          notificationsSuccess: true,
          notificationMethods: updatedMethods,
          originalNotificationData: JSON.stringify(updatedMethods),
        });

        // Persist updated fields to sessionStorage so a remount (e.g. returning
        // from the Features page) reloads the saved values instead of stale ones.
        // Mirrors legacy userService.SaveUserInformation() call.
        const currentSessionUser = getSessionUser();
        if (currentSessionUser) {
          setSessionUser({
            ...currentSessionUser,
            NotificationMethodId: payload.NotificationMethodId,
            NotificationEmailAddress: payload.EmailAddress,
            NotificationTextAreaCode: payload.TextAreaCode,
            NotificationTextPhoneNumber: payload.TextPhoneNumber,
            NotificationVoiceAreaCode: payload.VoiceAreaCode,
            NotificationVoicePhoneNumber: payload.VoicePhoneNumber,
            NotificationVoiceExtension: payload.VoiceExtension,
            EnableAllNotifications: enableAll,
          });
        }

        if (gv.isUsingGv) {
          await get().updateGuidedValueNotifications();
        }
      } else {
        set({
          notificationsSubmitting: false,
          notificationsError: result.StatusMessage ?? 'An error occurred updating your notifications.',
        });
      }
    } catch {
      set({
        notificationsSubmitting: false,
        notificationsError: 'An error occurred updating your notifications.',
      });
    }
  },

  clearNotificationsState: () => {
    set({ notificationsError: null, notificationsSuccess: false });
  },

  // ─── Guided Value ─────────────────────────────────────────────────────────

  fetchGuidedValue: async (memberId) => {
    set(state => ({ gv: { ...state.gv, loadingGv: true } }));

    try {
      const data = await getGuidedValueUserInfo(memberId);

      if (data.Status === 0 && data.Item && data.Item.length > 0) {
        const item = data.Item[0];
        const mobile = item.Identity?.Mobile;
        const { areaCode, number } = splitPhone(mobile);
        const maxorCustomerId = item.Id ?? null;

        set({
          gv: {
            isUsingGv: true,
            loadingGv: false,
            gvFetchAttempted: true,
            guidedValue: item,
            guidedValueTextAreaCode: areaCode,
            guidedValueTextNumber: number,
            maxorCustomerId,
          },
        });
      } else {
        set(state => ({ gv: { ...state.gv, isUsingGv: false, loadingGv: false, gvFetchAttempted: true } }));
      }
    } catch {
      // GV failure is silent — does not surface an error to the user
      set(state => ({ gv: { ...state.gv, isUsingGv: false, loadingGv: false, gvFetchAttempted: true } }));
    }
  },

  updateGuidedValueNotifications: async (onlyGv) => {
    const { notificationMethods, gv } = get();

    if (!gv.guidedValue || !gv.maxorCustomerId) return;

    // Mirror legacy logic: if text method selected and GV notify is on, use current text number
    // Otherwise use the GV's stored text number
    const methodId = notificationMethods.NotificationMethodId;
    const isTextMethod = methodId === 1001 || methodId === '1001';
    const gvNotify = gv.guidedValue.Preference?.GuidedValueNotify ?? false;

    let textNumber: string;
    if (isTextMethod && gvNotify) {
      textNumber = notificationMethods.NotificationTextAreaCode + notificationMethods.NotificationTextPhoneNumber;
    } else {
      textNumber = gv.guidedValueTextAreaCode + gv.guidedValueTextNumber;
    }

    // biome-ignore lint/suspicious/noExplicitAny: GV object shape is opaque — backend-defined
    const updatedGvItem: Record<string, any> = {
      ...gv.guidedValue,
      Identity: { ...(gv.guidedValue.Identity ?? {}), Mobile: textNumber },
    };

    const updatePayload = {
      CustomerId: gv.maxorCustomerId,
      // SessionUser email is not available in this store — use empty string; backend ignores it for auditing
      UpdatedByUser: '',
      UpdatedByApplication: `${window.mxAppConfig?.ClientName ?? ''} Member Portal`,
      TextNumber: textNumber,
      IsActive: gvNotify,
      MaxorCustomer: updatedGvItem,
    };

    set(state => ({ gv: { ...state.gv, loadingGv: true } }));

    try {
      const data = await postGuidedValue(updatePayload);

      if (data.Item) {
        const mobile = data.Item.Identity?.Mobile;
        const { areaCode, number } = splitPhone(mobile);
        set({
          gv: {
            ...get().gv,
            loadingGv: false,
            guidedValue: data.Item,
            guidedValueTextAreaCode: areaCode,
            guidedValueTextNumber: number,
          },
        });
      } else {
        set(state => ({ gv: { ...state.gv, loadingGv: false } }));
      }
    } catch {
      // GV update failure is silent — notification save already succeeded
      set(state => ({ gv: { ...state.gv, loadingGv: false } }));
    }

    if (onlyGv) {
      set({ notificationsSuccess: true });
    }
  },

  // ─── Reset ────────────────────────────────────────────────────────────────

  reset: () => {
    set({ ...initialState, gv: initialGv });
  },
}));
